'use client';

import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faCalendarRange, faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { ANALYTICS_VOLUME_YEAR_OPTIONS } from 'src/components/constants';

interface AnalyticsYearSelectorProps {
  gte: string;
  lte: string;
  onChange: (gte: string, lte: string) => void;
  className?: string;
}

const AnalyticsYearSelector: React.FC<AnalyticsYearSelectorProps> = ({
  gte,
  lte,
  onChange,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<'start' | 'end' | null>(
    null
  );
  const [localGte, setLocalGte] = useState(gte);
  const [localLte, setLocalLte] = useState(lte);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLocalGte(gte);
    setLocalLte(lte);
  }, [gte, lte]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
      });
    }
    setIsOpen(!isOpen);
    if (!isOpen) {
      setActiveDropdown(null);
    }
  };

  const validateDateRange = (
    gteValue: string,
    lteValue: string
  ): string | null => {
    const fromYear = parseInt(gteValue);
    const toYear = parseInt(lteValue);

    if (fromYear > toYear) {
      return 'From year cannot be greater than Through year';
    }

    return null;
  };

  const getValidationError = (): string | null => {
    return validateDateRange(localGte, localLte);
  };

  const handleYearClick = (type: 'start' | 'end', value: string) => {
    let newGte = localGte;
    let newLte = localLte;

    if (type === 'start') {
      newGte = value;
      setLocalGte(value);
    } else {
      newLte = value;
      setLocalLte(value);
    }

    // Check if the new range is valid
    const validationError = validateDateRange(newGte, newLte);

    // Only update the parent if the range is valid
    if (!validationError) {
      onChange(newGte, newLte);
    }

    setActiveDropdown(null);
  };

  const validationError = getValidationError();

  return (
    <div
      className={`min-w-36 w-full inline-flex flex-col justify-start items-start gap-1 relative ${className}`}
      ref={containerRef}
    >
      <div
        ref={buttonRef}
        className={`w-full px-4 bg-white rounded-full border ${
          validationError ? 'border-red-400' : 'border-neutral-200'
        } inline-flex justify-between items-center cursor-pointer hover:bg-neutral-100 h-9`}
        onClick={toggleDropdown}
      >
        <div className="flex justify-start items-center gap-1.5 flex-1">
          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
            <Icon icon={faCalendarRange} className="text-secondary w-3.5 h-3" />
          </div>
          <div className="self-stretch justify-start text-primary text-sm font-medium font-['Inter'] leading-5 line-clamp-1">
            {localGte}-{localLte}
          </div>
        </div>
        <Icon
          icon={faCaretDown}
          className={`text-neutral-500 w-4 h-4 ml-2 transition-all duration-300
          ${isOpen ? '-rotate-90' : 'rotate-0'}`}
        />
      </div>

      {isOpen &&
        dropdownPosition &&
        createPortal(
          <div
            ref={dropdownRef}
            className="fixed z-[99999] w-full min-w-[240px] bg-white border border-neutral-200 rounded-lg shadow-md p-4"
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: buttonRef.current?.offsetWidth || 240,
            }}
          >
            <div className="flex flex-col gap-4">
              {/* Year Range Selectors - Side by side layout */}
              <div className="pb-2 border-b border-neutral-200">
                <div className="flex items-center justify-center gap-2 mb-2">
                  {/* From Custom Select */}
                  <div className="flex flex-col gap-1 relative">
                    <label className="text-xs font-medium text-neutral-700">
                      From
                    </label>
                    <div className="relative">
                      <button
                        className="w-[90px] px-3 py-2 bg-white border border-neutral-200 rounded-full text-sm flex justify-between items-center"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveDropdown(
                            activeDropdown === 'start' ? null : 'start'
                          );
                        }}
                      >
                        <span>{localGte}</span>
                        <Icon
                          icon={faCaretDown}
                          className={`w-2.5 h-2.5 text-neutral-600 transition-all duration-300 ${
                            activeDropdown === 'start'
                              ? '-rotate-90'
                              : 'rotate-0'
                          }`}
                        />
                      </button>

                      {activeDropdown === 'start' && (
                        <div className="absolute top-full left-0 mt-1 w-full bg-white border border-neutral-200 rounded-lg shadow-md z-[99999]">
                          {ANALYTICS_VOLUME_YEAR_OPTIONS.map((option) => (
                            <div
                              key={`start-${option.value}`}
                              className={`px-4 py-2 hover:bg-neutral-100 cursor-pointer ${
                                localGte === option.value
                                  ? 'bg-neutral-100'
                                  : ''
                              }`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleYearClick('start', option.value);
                              }}
                            >
                              <span className="text-sm">{option.label}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mt-6 text-neutral-500">-</div>

                  {/* Through Custom Select */}
                  <div className="flex flex-col gap-1 relative">
                    <label className="text-xs font-medium text-neutral-700">
                      Through
                    </label>
                    <div className="relative">
                      <button
                        className="w-[90px] px-3 py-2 bg-white border border-neutral-200 rounded-full text-sm flex justify-between items-center"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveDropdown(
                            activeDropdown === 'end' ? null : 'end'
                          );
                        }}
                      >
                        <span>{localLte}</span>
                        <Icon
                          icon={faCaretDown}
                          className={`w-2.5 h-2.5 text-neutral-600 transition-transform duration-200 ${
                            activeDropdown === 'end' ? '-rotate-90' : 'rotate-0'
                          }`}
                        />
                      </button>

                      {activeDropdown === 'end' && (
                        <div className="absolute top-full left-0 mt-1 w-full bg-white border border-neutral-200 rounded-lg shadow-md z-[99999]">
                          {ANALYTICS_VOLUME_YEAR_OPTIONS.map((option) => (
                            <div
                              key={`end-${option.value}`}
                              className={`px-4 py-2 hover:bg-neutral-100 cursor-pointer ${
                                localLte === option.value
                                  ? 'bg-neutral-100'
                                  : ''
                              }`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleYearClick('end', option.value);
                              }}
                            >
                              <span className="text-sm">{option.label}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Validation Error Display */}
              {validationError && (
                <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-xs text-red-600 font-medium">
                    {validationError}
                  </p>
                </div>
              )}

              {/* Year Range description */}
              <div className="pt-1">
                <p className="text-xs font-bold text-neutral-900 leading-5">
                  Year Range
                </p>
                <p className="text-xs font-light text-neutral-600 leading-4">
                  Select the years to include in your results.
                </p>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default AnalyticsYearSelector;
